// KPI Overview grid card. Layout per the locked
// wireframe:
//
//   Title                                         RAG pill
//   Target ≥95%
//
//   90% · Apr 2026               [sparkline 100×36]
//   Compliance 50% · 12m avg 94%
//
//                                [trend chip ↓ 5pt vs Mar]
//
// The card is the primary entry point to the drawer; click + Enter /
// Space fire `onClick`. The title renders as a labelled div (not a
// heading) to match the mockup's label-style treatment and to avoid
// competing with the "Performance overview" h2 above the grid.
//
// Compliance + Xm-avg values are supplied as props so the panel can
// reuse its range state without the card needing to re-run the helpers
// on every render; both accept null when there's no history in range.

import type { JSX, KeyboardEvent } from "react";
import { HelpCircle } from "lucide-react";
import TrackingStatusBadge from "../../shared/TrackingStatusBadge";
import type {
  Kpi,
  KpiMeasurement,
  KpiReport,
  RagRating,
} from "../../../../../types";
import KpiSparkline from "./KpiSparkline";
import {
  formatKpiAverage,
  formatKpiTarget,
  formatKpiValue,
} from "../formatKpiValue";
import type { PeriodRange } from "../../../../../lib/kpi/summary";
import {
  formatPeriodLabel,
  normaliseFrequency,
} from "../../../../../lib/kpi/period";

export type KpiCardRag = RagRating | "neutral";

export interface KpiCardProps {
  kpi: Kpi;
  latestMeasurement?: KpiMeasurement;
  latestReport?: KpiReport;
  sparklinePoints: number[];
  rag: KpiCardRag;
  /** Range-aware compliance % for THIS kpi; null if no history in
   *  range. Drives the secondary line's "Compliance X%". */
  compliancePercent: number | null;
  /** Range-aware average KPI value; null if no numeric history in
   *  range. Drives the secondary line's "Xm avg Y". */
  averageValue: number | null;
  /** Active period range — drives the Xm-avg label ("30d avg" /
   *  "12m avg" / etc.). */
  range: PeriodRange;
  isSelected?: boolean;
  onClick: () => void;
}

type TrendDirection = "up" | "down" | "flat" | "unknown";

interface TrendDelta {
  direction: TrendDirection;
  /** Absolute change between first and last point, rounded to 1 dp.
   *  Undefined when first is 0 or direction is unknown/flat. The unit
   *  it carries depends on the KPI value type — see trendSuffix. */
  delta?: number;
  /** Whether the delta colour should read as good (true), bad (false)
   *  or neutral (null). */
  isGood: boolean | null;
}

function computeTrendDelta(
  points: number[],
  higherIsBetter?: boolean,
): TrendDelta {
  if (points.length < 2) {
    return { direction: "unknown", isGood: null };
  }
  const first = points[0];
  const last = points[points.length - 1];
  if (first === last) {
    return { direction: "flat", isGood: null };
  }
  const direction: TrendDirection = last > first ? "up" : "down";
  const delta = Math.round(Math.abs(last - first) * 10) / 10;
  let isGood: boolean | null = null;
  if (typeof higherIsBetter === "boolean") {
    isGood = direction === "up" ? higherIsBetter : !higherIsBetter;
  }
  return { direction, delta, isGood };
}

/** Unit suffix for the trend delta number. Percentage KPIs use "pt"
 *  (percentage points); number KPIs use the KPI's own unit with a
 *  leading space when set, otherwise nothing. Boolean KPIs suppress
 *  the chip entirely at the render site. */
function trendSuffix(kpi: Kpi): string {
  const valueType = kpi.valueType ?? "percentage";
  if (valueType === "percentage") return "pt";
  if (valueType === "number") return kpi.unit ? ` ${kpi.unit}` : "";
  return "";
}

function trendColourClass(isGood: boolean | null): string {
  if (isGood === true) return "text-emerald-600 dark:text-emerald-400";
  if (isGood === false) return "text-red-600 dark:text-red-400";
  return "text-slate-500 dark:text-slate-400";
}

function sparklineAriaLabel(
  kpiName: string,
  points: number[],
  higherIsBetter?: boolean,
): string {
  if (points.length === 0) return `${kpiName}: no trend data yet`;
  const first = points[0];
  const last = points[points.length - 1];
  if (first === last) return `${kpiName} trend, flat at ${last}`;
  const goingUp = last > first;
  let verb: string;
  if (typeof higherIsBetter !== "boolean") {
    verb = goingUp ? "trending up" : "trending down";
  } else if (goingUp === higherIsBetter) {
    verb = "improving";
  } else {
    verb = "declining";
  }
  return `${kpiName} trend, ${verb} from ${first} to ${last}`;
}

/** Label for the Xm-avg secondary segment — matches the summary
 *  strip range overline style but in lower-case prose. */
function rangeAvgLabel(range: PeriodRange): string {
  switch (range) {
    case "30d":
      return "30d avg";
    case "90d":
      return "90d avg";
    case "12m":
      return "12m avg";
    case "ytd":
      return "YTD avg";
  }
}

/** Short form of the latest period — "Apr 2026" / "Q1 2026" /
 *  "Mon 01 Apr" etc. Used in the hero row next to the value. */
function latestPeriodLabel(
  latestMeasurement: KpiMeasurement | undefined,
  kpi: Kpi,
): string | null {
  if (!latestMeasurement?.period) return null;
  const freq = normaliseFrequency(
    kpi.measurementFrequency ?? "monthly",
  );
  return formatPeriodLabel(latestMeasurement.period, freq);
}

// Hero value + unit come from the shared `formatKpiValue` formatter
// so card, drawer, history, and bulk-modal targets all
// agree on per-valueType rendering (Met / Not met, `X hrs`, `99.5%`).

export default function KpiCard({
  kpi,
  latestMeasurement,
  latestReport,
  sparklinePoints,
  rag,
  compliancePercent,
  averageValue,
  range,
  isSelected = false,
  onClick,
}: KpiCardProps): JSX.Element {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  const hero = formatKpiValue(kpi, latestMeasurement);
  const periodLabel = latestPeriodLabel(latestMeasurement, kpi);
  const trend = computeTrendDelta(sparklinePoints, kpi.higherIsBetter);
  const isBoolean = (kpi.valueType ?? "percentage") === "boolean";
  // Boolean KPIs plot as 100 / 0 on the sparkline, so a numeric delta
  // would read as "100" / "100pt" which isn't meaningful. Suppress the
  // chip — the sparkline + RAG pill already carry the trend signal.
  const showTrendChip =
    !isBoolean &&
    (trend.direction === "up" ||
      trend.direction === "down" ||
      trend.direction === "flat");
  const suffix = trendSuffix(kpi);

  // Build the secondary line dynamically — hide segments that have
  // no data so the card doesn't read "Compliance — · 12m avg —".
  // `formatKpiAverage` returns null for boolean KPIs so the 12m-avg
  // segment drops cleanly without a type flag.
  const hasCompliance = compliancePercent !== null;
  const avgFormatted = formatKpiAverage(kpi, averageValue);
  const hasAverage = avgFormatted !== null;
  const averageText = hasAverage ? `${rangeAvgLabel(range)} ${avgFormatted}` : null;
  const showSecondary = hasCompliance || hasAverage;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={`group cursor-pointer rounded-md border bg-white p-3.5 transition-all hover:-translate-y-px hover:shadow-md focus:outline-hidden focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 dark:bg-slate-800/60 dark:focus:ring-offset-slate-900 ${
        isSelected
          ? "border-brand-400 ring-2 ring-brand-400 dark:border-brand-400"
          : "border-slate-200 dark:border-slate-700"
      }`}
      data-testid={`kpi-card-${kpi.id}`}
      data-selected={isSelected ? "true" : undefined}
      aria-label={kpi.name}
      aria-pressed={isSelected}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
            {kpi.name}
          </div>
          <div className="mt-0.5 text-caption text-slate-500 dark:text-slate-400">
            Target {formatKpiTarget(kpi)}
          </div>
        </div>
        {latestReport ? (
          <TrackingStatusBadge kind="rag" rating={latestReport.ragRating} />
        ) : (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-caption text-slate-600 dark:bg-slate-700 dark:text-slate-300"
            aria-label="Not assessed"
          >
            <span
              className="h-2 w-2 rounded-full bg-slate-400"
              aria-hidden="true"
            />
            Not assessed
          </span>
        )}
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div
            className="flex items-baseline gap-1 text-slate-900 dark:text-slate-100"
            data-testid="kpi-card-hero"
          >
            <span
              className="text-h2 leading-none"
              data-testid="kpi-card-value"
            >
              {hero.numeric}
            </span>
            {hero.unit && (
              <span
                className="text-h4 leading-none text-slate-500 dark:text-slate-400"
                data-testid="kpi-card-unit"
              >
                {hero.unit.trim()}
              </span>
            )}
            {periodLabel && (
              <span
                className="ml-1 text-caption text-slate-500 dark:text-slate-400"
                data-testid="kpi-card-period"
              >
                · {periodLabel}
              </span>
            )}
          </div>
          {showSecondary && (
            <div
              className="mt-1 flex flex-wrap items-center gap-1 text-caption text-slate-500 dark:text-slate-400"
              data-testid="kpi-card-secondary"
            >
              {hasCompliance && (
                <>
                  <span>Compliance {compliancePercent}%</span>
                  {/* Clicking the icon should NOT open the drawer —
                      stop the bubble so the parent `role=button` card
                      click handler stays a clean row-click surface. */}
                  <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                      }
                    }}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-slate-400 hover:text-slate-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-500 dark:hover:text-slate-300"
                    aria-label="About compliance calculation"
                    title="Weighted average across reporting periods: green counts fully, amber counts as partial credit (50%), red counts as zero."
                    data-testid="kpi-card-compliance-help"
                  >
                    <HelpCircle
                      aria-hidden="true"
                      className="h-3.5 w-3.5"
                    />
                  </button>
                </>
              )}
              {hasCompliance && hasAverage && <span aria-hidden="true">·</span>}
              {hasAverage && <span>{averageText}</span>}
            </div>
          )}
        </div>
        {sparklinePoints.length > 0 ? (
          <KpiSparkline
            points={sparklinePoints}
            rag={rag}
            ariaLabel={sparklineAriaLabel(
              kpi.name,
              sparklinePoints,
              kpi.higherIsBetter,
            )}
          />
        ) : (
          <div className="h-[36px] w-[100px] rounded-sm bg-slate-100 dark:bg-slate-800">
            <span className="sr-only">No trend data yet</span>
          </div>
        )}
      </div>

      {showTrendChip && typeof trend.delta === "number" && (
        <div className="mt-3 flex justify-end">
          <span
            className={`inline-flex items-center gap-1 text-caption ${trendColourClass(trend.isGood)}`}
            data-testid="kpi-card-trend"
          >
            <span aria-hidden="true">
              {trend.direction === "flat"
                ? "—"
                : trend.direction === "up"
                  ? "↑"
                  : "↓"}
            </span>
            <span>
              {trend.delta}
              {suffix}
            </span>
            {periodLabel && trend.direction !== "flat" && (
              <span className="text-slate-500 dark:text-slate-400">
                vs prev
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
